import type { PublicSplit } from '../../types.ts';
import { unnamedVenue } from '../../venue-name.ts';
import { brand } from '../../brand.ts';
import { app, logo, esc, inputValue, privacyLink, target, toast } from '../dom.ts';
import { friend } from '../api.ts';
import { saved } from '../saved.ts';
import { trackOnce } from '../track.ts';
import { COME_BACK, OCCASIONS } from '../../types.ts';
import { ls } from '../dom.ts';
import { fmt } from '../../money.ts';

export function errorScreen(message: string): void {
  app.innerHTML = `
    <div class="bar">${logo}</div>
    <div class="body"><h1 class="h">Hmm.</h1><p class="lede">${esc(message)}</p><a class="btn2" href="/" data-go>Go to start</a></div>`;
}

/** How many people the payer said are at the table but haven't joined yet. */
export const missingCount = (s: PublicSplit): number => (s.partySize ? Math.max(0, s.partySize - s.people.length) : 0);

export const tipPercent = (s: PublicSplit): number => {
  const sub = s.items.reduce((a, i) => a + i.cents, 0);
  return sub > 0 ? Math.round((s.tipCents / sub) * 100) : 0;
};

export const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;

/** Names the fees folded into tax ("Includes Historic Preservation Fee $24.06…"), or nothing. */
export const feeNote = (s: PublicSplit): string =>
  s.fees?.length
    ? `<div class="sub" style="font-size:13px;padding:0 0 6px">Includes ${s.fees.map((f) => `${esc(f.name)} ${fmt(f.cents)}`).join(', ')} on the bill, shared by everyone by what they ordered</div>`
    : '';

// ---------- restaurant offers by text ----------
// Asked after the money part is done, never before. The box starts unchecked. The restaurant
// gets the number only if the guest ticks it. Nothing is added automatically.

export const consent = (venue: string) =>
  `Share my number with ${venue} so they can text me. Up to 4 texts a month. Msg & data rates may apply. Reply STOP to opt out anytime.`;

/** The yes is an unchecked box. Nothing is shared until the guest ticks it. */
export function optInBox(venue: string): string {
  return `<label class="opt-check"><input id="optYes" type="checkbox"><span>Yes, ${esc(venue)} can have my number and text me. Reply STOP to opt out.</span></label>`;
}

/** "The bill" is what a split is called when nobody typed the restaurant. There's no one to get deals from then. */
export const knownVenue = (s: PublicSplit): boolean => !unnamedVenue(s.venue);

export function optInCard(s: PublicSplit): string {
  if (!knownVenue(s)) return '';
  trackOnce('optin:' + s.code, 'optin_shown', { code: s.code });
  if (saved.optedIn(s.code)) {
    return `<div class="optin done"><div><b>You're on the list for ${esc(s.venue)}.</b><div class="sub">They have your number and can text you. Reply STOP to opt out.</div></div>
      <button class="link" data-act="optOut">Undo</button></div>`;
  }
  return `<div class="optin">
      <div style="font-weight:600">Want deals from ${esc(s.venue)}?</div>
      <p class="sub" style="margin:0">${esc(consent(s.venue))} We never sell your number. ${privacyLink()}</p>
      ${optInBox(s.venue)}
      <div class="optin-row">
        <input id="optPhone" class="input" type="tel" inputmode="tel" autocomplete="tel" placeholder="Your phone number" value="${esc(saved.phone())}" aria-label="Phone number for offers">
        <button class="btn2" data-act="optIn">Text me deals</button>
      </div>
    </div>`;
}

/** Handles taps on the card. Returns true when the tap was the card's. */
export async function optInClick(e: Event, s: PublicSplit, token: string | undefined, redraw: () => void): Promise<boolean> {
  const b = target<HTMLButtonElement>(e, '[data-act=optIn], [data-act=optOut]');
  if (!b || !token) return false;
  const on = b.dataset.act === 'optIn';
  const box = document.getElementById('optYes') as HTMLInputElement | null;
  if (on && !box?.checked) { toast('Tick the box first. Nothing is shared until you do.'); return true; }
  const phone = inputValue('optPhone');
  if (on && phone.replace(/\D/g, '').length < 7) { toast('Add your phone number first.'); return true; }
  b.disabled = true;
  try {
    await friend.optIn(s.code, token, on, phone);
    saved.setOptedIn(s.code, on);
    if (on) saved.setPhone(phone);
    toast(on ? `You're on ${s.venue}'s list` : 'Taken off the list');
  } catch (err) {
    toast((err as Error).message);
  }
  redraw();
  return true;
}

// ---------- three-tap survey for the restaurant ----------
// Optional, after the money is done. Each tap saves on its own, so one answer is still useful.

type SurveyState = { rating?: number; occasion?: string; comeBack?: string; skipped?: boolean };
const surveyState = (code: string) => ls.get<SurveyState>('survey:' + code, {});

/** A friend only gets the one-tap "How was it?": done once they tap a face or skip. */
export const ratedOrSkipped = (code: string): boolean => { const st = surveyState(code); return Boolean(st.rating || st.skipped); };

export function surveyCard(s: PublicSplit): string {
  const st = surveyState(s.code);
  if (st.skipped) return '';
  trackOnce('survey:' + s.code, 'survey_shown', { code: s.code });
  if (st.rating && st.occasion && st.comeBack) {
    return `<div class="optin done"><div><b>Thanks!</b><div class="sub">${knownVenue(s) ? esc(s.venue) : 'The restaurant'} only ever sees everyone's answers added up, never yours.</div></div></div>`;
  }
  const chips = (key: 'occasion' | 'comeBack', list: readonly string[]) =>
    `<div class="chips survey-chips">${list.map((o) => `<button class="chip" data-survey="${key}" data-v="${esc(o)}" aria-pressed="${st[key] === o}">${esc(o)}</button>`).join('')}</div>`;
  return `<div class="optin survey">
      <div style="display:flex;justify-content:space-between;align-items:baseline;gap:8px"><div style="font-weight:600">Help ${knownVenue(s) ? esc(s.venue) : 'the restaurant'} out?</div><button class="link" data-survey="skip" style="padding:0;font-size:13px">Skip</button></div>
      <div class="sub">How was it?</div>
      <div class="faces">${['😞', '🙁', '😐', '🙂', '😍'].map((f, i) => `<button data-survey="rating" data-v="${i + 1}" aria-pressed="${st.rating === i + 1}" aria-label="${i + 1} of 5">${f}</button>`).join('')}</div>
      ${st.rating ? `<div class="sub">What was the occasion?</div>${chips('occasion', OCCASIONS)}` : ''}
      ${st.rating && st.occasion ? `<div class="sub">What would bring you back sooner?</div>${chips('comeBack', COME_BACK)}` : ''}
    </div>`;
}

/** Handles taps on the survey card. Returns true when the tap was the card's. */
export async function surveyClick(e: Event, s: PublicSplit, token: string | undefined, redraw: () => void): Promise<boolean> {
  const b = target<HTMLButtonElement>(e, '[data-survey]');
  if (!b) return false;
  const key = b.dataset.survey as 'rating' | 'occasion' | 'comeBack' | 'skip';
  const st = surveyState(s.code);
  if (key === 'skip') { ls.set('survey:' + s.code, { ...st, skipped: true }); redraw(); return true; }
  if (!token) return true;
  const v = b.dataset.v || '';
  const next = { ...st, [key]: key === 'rating' ? Number(v) : v };
  ls.set('survey:' + s.code, next);
  redraw();
  try { await friend.survey(s.code, token, { [key]: key === 'rating' ? Number(v) : v }); } catch (err) { toast((err as Error).message); }
  return true;
}
